'use client';
import { spacing, theme } from '@cc-shopping/ui/theme';
import {
  Box,
  type BoxProps,
  Checkbox,
  FormControlLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { forwardRef, useMemo } from 'react';
import { useCompareBar } from './Context';
import type { FireCheckboxAnalyticsEventsType } from './types';

type AddToCompareBarProps = Partial<BoxProps> & {
  creditCardOfferId: string;
  fireAnalyticsEvents: FireCheckboxAnalyticsEventsType;
  handleTouchStart?: () => void;
  variant: 'design1' | 'design2';
};

export const AddToCompareCheckbox = forwardRef<HTMLDivElement, AddToCompareBarProps>(
  ({ creditCardOfferId, fireAnalyticsEvents, handleTouchStart, variant, ...props }, ref) => {
    const { addIdToCompare, removeIdToCompare, idsToCompare } = useCompareBar();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const addedToCompare = idsToCompare.includes(creditCardOfferId);
    const disableCheckbox = isMobile ? idsToCompare.length >= 2 : idsToCompare.length === 3;

    const containerDesign1SxMemoized = useMemo(
      () => ({
        cursor: 'pointer',
        outline: addedToCompare
          ? `1px solid #00682C`
          : `1px solid ${theme.palette.neutral.lighter}`,
        '&:hover':
          !addedToCompare && !disableCheckbox
            ? {
                backgroundColor: theme.palette.blues.lightest,
                outline: `2px solid ${theme.palette.secondary.main}`,
              }
            : null,
      }),
      [addedToCompare, disableCheckbox],
    );

    const containerDesign2SxMemoized = useMemo(
      () => ({
        cursor: 'pointer',
        outline: addedToCompare ? `2px solid ${theme.palette.primary.main}` : 'none',
        '&:hover':
          !addedToCompare && !disableCheckbox
            ? {
                backgroundColor: '#EBEBEC',
              }
            : null,
      }),
      [addedToCompare, disableCheckbox],
    );

    const checkboxSxMemoized = useMemo(
      () => ({
        padding: 0,
        mr: spacing(2),
        color: variant === 'design1' ? 'none' : 'common.black',
        mt: variant === 'design1' ? 0 : '-1px',
      }),
      [],
    );

    return (
      <Box
        ref={ref}
        bgcolor={variant === 'design1' ? 'common.white' : 'neutral.lightest'}
        borderRadius={variant === 'design1' ? 0 : '2px'}
        height="fit-content"
        sx={variant === 'design1' ? containerDesign1SxMemoized : containerDesign2SxMemoized}
        {...(isMobile && disableCheckbox ? { onTouchStart: handleTouchStart } : {})}
        {...props}
      >
        <FormControlLabel
          sx={{
            m: 0,
            px: variant === 'design1' ? 1.5 : 2,
            py: variant === 'design1' ? 2 : 1,
          }}
          control={
            <Checkbox
              disabled={!addedToCompare && disableCheckbox}
              color="primary"
              size="medium"
              sx={checkboxSxMemoized}
              checked={addedToCompare}
              icon={
                <span
                  aria-hidden="true"
                  className="mask-icon mask-icon-checkbox-blank"
                  style={{
                    width: variant === 'design1' ? 20 : 18,
                    height: variant === 'design1' ? 20 : 18,
                  }}
                />
              }
              checkedIcon={
                <span
                  aria-hidden="true"
                  className="mask-icon mask-icon-checkbox-outlined"
                  style={{
                    width: variant === 'design1' ? 20 : 18,
                    height: variant === 'design1' ? 20 : 18,
                    borderRadius: '4px',
                  }}
                />
              }
              onClick={e => e.stopPropagation()}
              onChange={event => {
                if (event.target.checked) {
                  fireAnalyticsEvents.select();
                  addIdToCompare(creditCardOfferId);
                } else {
                  fireAnalyticsEvents.unselect();
                  removeIdToCompare(creditCardOfferId);
                }
              }}
              aria-label="Add to compare"
            />
          }
          label={
            <Typography
              component="span"
              sx={{
                color:
                  !addedToCompare && disableCheckbox
                    ? theme.palette.neutral.lighter
                    : addedToCompare && variant === 'design2'
                      ? theme.palette.primary.main
                      : theme.palette.common.black,

                fontSize: { xs: variant === 'design1' ? 16 : 12, sm: 14 },
                whiteSpace: 'nowrap',
              }}
            >
              {addedToCompare
                ? variant === 'design2' && isMobile
                  ? 'Added'
                  : 'Added to compare'
                : 'Add to compare'}
            </Typography>
          }
        />
      </Box>
    );
  },
);
